import { useDispatch, useSelector } from "react-redux";

import HorizontalBarChart from "@/components/ui/chart/HorizontalBarChart";
import Dropdown from "@/components/ui/dropdown";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { RootState } from "@/store";
import { setGridsSelected } from "@/store/slice/analysisSummary";

import GeneralSummary from "./GeneralSummary";

type OverviewProps = {
  isExpanded: boolean;
  chartRef: React.RefObject<HTMLDivElement>;
};

export default function Overview({ isExpanded, chartRef }: OverviewProps) {
  const dispatch = useDispatch();

  const {
    GRID_DATA,
    ATTRIBUTE_OPTIONS,
    handleAttributeChange,
    getTypeSummary
  } = useAnalysisSummaryDataContext();

  const { generalSummary, selectedAttribute } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const handleOnBarClick = (name: string) => {
    dispatch(setGridsSelected([name]));
  };

  return (
    <div className="flex flex-col">
      <GeneralSummary
        quoteTxt={
          isExpanded
            ? `Overview of the spatial distribution of Point of Interest(POI) based
          on industry classifications, locations, and geographic spread.`
            : null
        }
      />

      <div
        className="flex flex-row items-center justify-between px-3 py-2 mt-2 mb-4 font-semibold capitalize bg-white border border-gainsboro rounded-xl"
        role="region"
        aria-label={`Total ${getTypeSummary()} Summary`}
      >
        <p>Total {getTypeSummary()}</p>
        <p data-testid="total-grid-label">
          {generalSummary?.data.general.total_rows}
        </p>
      </div>

      <div className="flex flex-col flex-1">
        <div className="flex flex-row items-center justify-between mb-3">
          <h1 className="text-sm font-semibold text-txtHeading">
            Top 10 {getTypeSummary()} by{" "}
            <span className="capitalize">{selectedAttribute.label}</span>
          </h1>

          <Dropdown
            className="max-w-[132px] w-full"
            name="attribute"
            placeholder="Select Attribute"
            data={ATTRIBUTE_OPTIONS}
            onSelect={handleAttributeChange}
            value={selectedAttribute}
            defaultValue={{ label: "Score", value: "score" }}
          />
        </div>

        {GRID_DATA.length > 0 ? (
          <HorizontalBarChart
            ref={chartRef}
            data={GRID_DATA}
            title={`Top 10 ${getTypeSummary()} by ${selectedAttribute.label}`}
            onBarClick={handleOnBarClick}
          />
        ) : (
          <div className="flex items-center justify-center flex-1 px-10 font-bold text-center text-txtHeading">
            No data available to display.
          </div>
        )}
      </div>
    </div>
  );
}
